import React, { useState, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Upload, FileArrowUp, CheckCircle, XCircle, DownloadSimple, ArrowsClockwise, WarningCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ImportRCUPage = () => {
  const { axiosInstance } = useAuth();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const ext = selectedFile.name.toLowerCase();
      if (ext.endsWith('.csv') || ext.endsWith('.zip')) {
        setFile(selectedFile);
        setResult(null);
      } else {
        toast.error('Formato non supportato. Usa CSV o ZIP.');
      }
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      const ext = droppedFile.name.toLowerCase();
      if (ext.endsWith('.csv') || ext.endsWith('.zip')) {
        setFile(droppedFile);
        setResult(null);
      } else {
        toast.error('Formato non supportato. Usa CSV o ZIP.');
      }
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axiosInstance.post('/rcu/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(data);
      if (data.success) {
        toast.success(`Import RCU completato! ${data.imported} record importati.`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore import RCU');
    } finally {
      setUploading(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const comp = result?.comparison;

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="import-rcu-page">
      <div>
        <h1 className="text-4xl font-extrabold tracking-tighter text-slate-900">Import RCU (SII)</h1>
        <p className="mt-2 text-sm text-slate-600">
          Importa l'anagrafica RCU dal Sistema Informativo Integrato (Acquirente Unico) per confronto con i POD dichiarati
        </p>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-sm p-4">
        <h3 className="text-sm font-semibold text-amber-900 mb-2">Nota sul Flusso Temporale</h3>
        <p className="text-xs text-amber-700">
          L'anagrafica POD importata manualmente anticipa il dato SII. L'RCU viene pubblicato da Acquirente Unico al giorno +3/5
          del mese N. Le denunce vanno presentate entro fine mese -1 rispetto all'inizio fornitura
          (es. 31/03 per partenze del 01/04). L'import RCU serve come verifica/confronto successivo.
        </p>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-sm p-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-blue-900">Formato File Accettato</h3>
          <Button
            size="sm"
            variant="outline"
            onClick={() => window.open(`${API_URL}/api/rcu/template`, '_blank')}
            className="rounded-sm text-xs border-blue-300 text-blue-700 hover:bg-blue-100"
            data-testid="download-rcu-template"
          >
            <DownloadSimple size={14} className="mr-1" />
            Scarica Template
          </Button>
        </div>
        <p className="text-xs text-blue-700">
          CSV (separatore ;) o ZIP contenente CSV. Colonne riconosciute: POD, RAGIONE_SOCIALE, CF, PIVA,
          PROVINCIA, COMUNE, DATA_INIZIO, DATA_FINE, TIPOLOGIA_USO, STATO
        </p>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm p-8">
        <div
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          className="border-2 border-dashed border-slate-300 rounded-sm p-12 text-center hover:border-[#0066FF] hover:bg-slate-50 transition-all cursor-pointer"
          onClick={() => fileInputRef.current?.click()}
          data-testid="rcu-file-dropzone"
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.zip"
            onChange={handleFileChange}
            className="hidden"
            data-testid="rcu-file-input"
          />
          {file ? (
            <div>
              <FileArrowUp size={48} className="mx-auto text-green-600 mb-4" />
              <p className="text-sm font-medium text-slate-900 mb-1">{file.name}</p>
              <p className="text-xs text-slate-600">{(file.size / 1024).toFixed(2)} KB</p>
            </div>
          ) : (
            <div>
              <Upload size={48} className="mx-auto text-slate-300 mb-4" />
              <p className="text-sm font-medium text-slate-900 mb-1">Trascina file CSV/ZIP o clicca per selezionare</p>
              <p className="text-xs text-slate-600">CSV o ZIP (Max 50 MB)</p>
            </div>
          )}
        </div>
        <div className="mt-6 flex gap-3">
          <Button
            onClick={handleUpload}
            disabled={!file || uploading}
            className="flex-1 bg-[#0A2540] hover:bg-[#0A2540]/90 text-white rounded-sm"
            data-testid="rcu-upload-button"
          >
            <ArrowsClockwise size={16} className={`mr-2 ${uploading ? 'animate-spin' : ''}`} />
            {uploading ? 'Importazione e confronto in corso...' : 'Importa e Confronta con POD'}
          </Button>
          {file && (
            <Button onClick={handleReset} variant="outline" className="rounded-sm">Reset</Button>
          )}
        </div>
      </div>

      {comp && (
        <div className="space-y-4" data-testid="rcu-comparison-result">
          <div className="bg-white border border-slate-200 rounded-sm p-6">
            <h2 className="text-xl font-bold tracking-tight text-slate-900 mb-4">
              <ArrowsClockwise size={20} className="inline mr-2" />
              Risultato Confronto RCU vs POD Dichiarati
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-sm text-center">
                <p className="text-xs text-blue-600 font-semibold mb-1">POD Dichiarati</p>
                <p className="text-2xl font-bold text-blue-900">{comp.summary.our_total}</p>
              </div>
              <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-sm text-center">
                <p className="text-xs text-indigo-600 font-semibold mb-1">POD nel RCU</p>
                <p className="text-2xl font-bold text-indigo-900">{comp.summary.rcu_total}</p>
              </div>
              <div className="p-3 bg-green-50 border border-green-200 rounded-sm text-center">
                <p className="text-xs text-green-600 font-semibold mb-1">Corrispondenti</p>
                <p className="text-2xl font-bold text-green-900">{comp.summary.matched}</p>
              </div>
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-sm text-center">
                <p className="text-xs text-amber-600 font-semibold mb-1">Solo nei nostri</p>
                <p className="text-2xl font-bold text-amber-900">{comp.summary.only_in_our_db}</p>
              </div>
              <div className="p-3 bg-red-50 border border-red-200 rounded-sm text-center">
                <p className="text-xs text-red-600 font-semibold mb-1">Solo nel RCU</p>
                <p className="text-2xl font-bold text-red-900">{comp.summary.only_in_rcu}</p>
              </div>
            </div>

            {comp.summary.only_in_our_db > 0 && (
              <div className="mb-4">
                <h3 className="text-sm font-semibold text-amber-800 mb-2 flex items-center gap-1">
                  <WarningCircle size={16} />
                  POD dichiarati ma non presenti nel RCU (non ancora registrati al SII)
                </h3>
                <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 max-h-40 overflow-y-auto">
                  <div className="flex flex-wrap gap-2">
                    {comp.only_in_our_db.map((pod) => (
                      <span key={pod} className="text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded font-mono">{pod}</span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {comp.summary.only_in_rcu > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-red-800 mb-2 flex items-center gap-1">
                  <WarningCircle size={16} />
                  POD nel RCU ma non nei nostri dati (da verificare)
                </h3>
                <div className="bg-red-50 border border-red-200 rounded-sm p-3 max-h-40 overflow-y-auto">
                  <div className="flex flex-wrap gap-2">
                    {comp.only_in_rcu.map((pod) => (
                      <span key={pod} className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded font-mono">{pod}</span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {comp.summary.matched === comp.summary.our_total && comp.summary.only_in_rcu === 0 && (
              <div className="flex items-center gap-2 p-4 bg-green-50 border border-green-200 rounded-sm">
                <CheckCircle size={24} weight="fill" className="text-green-600" />
                <p className="text-sm font-medium text-green-800">Tutti i POD dichiarati corrispondono al RCU. Nessuna discrepanza.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ImportRCUPage;
